import "./project-specs.jsx";

/* project-detail.jsx — redesigned Project Detail (Direction B), wired to the
   live backend through the same `onDrawer` / `onAction` contract the original
   ProjectDetail used. Drop-in replacement: render <ProjectDetailView db,
   project, onBack, onDrawer, onAction /> wherever the old ProjectDetail was.

   Reuses existing app primitives: Panel, DataTable, Pill, Avatar, KPI, Icon,
   Toolbar, money/fmtDate (window.AC), MODEL. Planning tab lives in
   project-planning.jsx (window.ProjectPlanningExec). Lifeline + ring live in
   project-lifeline.jsx.

   NOTE: inline edits of project fields (name/kind/status/description) call
   onAction('updateProject', { projectId, project, patch }). Add that one case
   to app.jsx (see README). All other actions already exist. */
(function () {
  const { useState } = React;
  const { money, fmtDate } = window.AC;
  const MODEL = window.MODEL;

  const decimal = (v) => { const n = Number(v); return Number.isFinite(n) ? n.toString() : '0'; };
  const percent = (v) => `${decimal((Number(v) || 0) * 100)}%`;
  const projectTabFromHash = (projectId) => {
    const match = window.location.hash.match(/^#\/?projects\/([^/]+)\/([^/]+)/);
    if (!match || decodeURIComponent(match[1]) !== projectId) return 'dashboard';
    const tab = match[2] === 'requirements' ? 'design' : match[2];
    return ['dashboard', 'context', 'design', 'specs', 'board', 'testing', 'calendar', 'email', 'financials'].includes(tab) ? tab : 'dashboard';
  };

  /* ---------- finance helpers (identical to the original cards.jsx) ---------- */
  function projectFinance(db, projectId) {
    const project = db.projects.find((p) => p.id === projectId);
    const deliverables = new Set((project ? project.projectDeliverables : []).map((s) => s.id));
    const invs = db.invoices.filter((iv) => (iv.lines || []).some((line) => line.projectId === projectId || deliverables.has(line.projectDeliverableId)));
    const expenses = (db.expenses || []).filter((expense) => expense.projectId === projectId);
    let billed = 0, outstanding = 0;
    invs.forEach((iv) => { const e = MODEL.enrich(db, iv); if (!MODEL.isDraft(iv) && !MODEL.isCancelled(iv)) billed += e.totals.total; if (e.open) outstanding += e.totals.outstanding; });
    const spend = expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0) + (Number(expense.taxAmount) || 0), 0);
    return { invs, expenses, billed, outstanding, spend, net: billed - spend };
  }
  function customerOutstanding(db, customerId) {
    let out = 0;
    db.invoices.filter((iv) => iv.customerId === customerId).forEach((iv) => { const e = MODEL.enrich(db, iv); if (e.open) out += e.totals.outstanding; });
    return out;
  }
  const offerTotals = (offer) => {
    const subtotal = (offer.lines || []).reduce((s, l) => s + l.quantity * l.unitPrice, 0);
    const tax = (offer.lines || []).reduce((s, l) => s + l.quantity * l.unitPrice * l.taxRate, 0);
    return { subtotal, tax, total: subtotal + tax };
  };
  function projectDeliverableRows(project, db) {
    const invoices = db.invoices || [];
    return [
      ...project.projectDeliverables.map((deliverable) => {
        const milestoneIds = deliverable.milestoneIds || [];
        const milestoneBillable = milestoneIds.length === 0 || milestoneIds.every((id) => (project.milestones || []).some((m) => m.id === id && m.completed));
        const existingInvoices = invoices.filter((iv) => (iv.lines || []).some((l) => l.projectDeliverableId === deliverable.id));
        const existingDates = new Set(existingInvoices.map((iv) => String(iv.issueDate).slice(0, 10)));
        const serviceSchedule = { ...deliverable, date: deliverable.billingStartDate };
        const missingInvoiceCount = deliverable.freeOfCharge || !milestoneBillable ? 0
          : deliverable.recurrenceFrequency === 'None' && existingInvoices.length ? 0
          : MODEL.expenseOccurrences(serviceSchedule).filter((d) => !existingDates.has(d)).length;
        return { type: 'deliverable', id: 'deliverable-' + deliverable.id, deliverable, missingInvoiceCount, milestoneBillable };
      }),
      ...(project.offers || []).map((offer) => ({ type: 'offer', id: 'offer-' + offer.id, offer })),
    ];
  }
  function progressOf(project) {
    const ms = project.milestones || [];
    const done = ms.filter((m) => m.completed).length;
    let tt = 0, td = 0;
    (project.workPackages || []).forEach((s) => {
      (s.ticketClusters || []).forEach((cluster) => (cluster.tickets || []).forEach((ticket) => { tt++; if (ticket.done) td++; }));
    });
    return { msDone: done, msTotal: ms.length, msPct: ms.length ? done / ms.length : 0, ticketDone: td, ticketTotal: tt };
  }

  function projectAiContextReference(project, customers, progress, finance) {
    return {
      type: 'Project',
      id: project.id,
      label: project.name || 'Untitled project',
      url: `${window.location.origin}${window.location.pathname}#/projects/${encodeURIComponent(project.id)}`,
      fields: {
        Kind: project.kind || '',
        Status: project.status || '',
        Customers: customers,
        Deliverables: (project.projectDeliverables || []).length,
        Milestones: `${progress.msDone}/${progress.msTotal}`,
        Tickets: `${progress.ticketDone}/${progress.ticketTotal}`,
        Billed: money(finance.billed),
        Outstanding: finance.outstanding > 0 ? money(finance.outstanding) : '',
        Repository: project.projectRepoUrl || '',
        App: project.projectAppUrl || '',
      },
    };
  }

  /* ---------- inline editors ---------- */
  function Inline({ value, onCommit, multiline, placeholder, className, style, rows = 6 }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(value);
    React.useEffect(() => { setVal(value); }, [value]);
    const commit = () => { setEditing(false); if (val !== value) onCommit(val); };
    const cancel = () => { setVal(value); setEditing(false); };
    if (editing) {
      if (multiline) return <textarea className="inl-area" autoFocus rows={rows} value={val} style={style} onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Escape') cancel(); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit(); }} />;
      return <input className="inl-input" autoFocus value={val} style={style} onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }} />;
    }
    return <span className={'inl ' + (className || '')} tabIndex={0} style={style} onClick={() => setEditing(true)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditing(true); } }}>{value ? value : <span className="faint">{placeholder || 'Add…'}</span>}</span>;
  }

  function StatusPick({ value, options, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);
    React.useEffect(() => {
      if (!open) return;
      const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('pointerdown', off, true);
      return () => document.removeEventListener('pointerdown', off, true);
    }, [open]);
    return (
      <span className="statuspick" ref={ref}>
        <button style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }} onClick={() => setOpen((o) => !o)} title="Change status"><Pill kind={value} sm /></button>
        {open && <div className="statuspick-menu">{options.map((o) => <button key={o} onClick={() => { onChange(o); setOpen(false); }}><Pill kind={o} sm /></button>)}</div>}
      </span>
    );
  }

  function projectDocumentsFor(project, target = {}) {
    const docs = project && project.documents ? project.documents : [];
    return docs.filter((doc) =>
      (target.workPackageId ? doc.workPackageId === target.workPackageId : !doc.workPackageId)
      && (target.milestoneId ? doc.milestoneId === target.milestoneId : !doc.milestoneId)
      && (target.ticketId ? doc.ticketId === target.ticketId : !doc.ticketId));
  }

  function projectDocumentTitle(doc) {
    const parts = [doc.fileName];
    if (doc.externalProvider) parts.push(`External: ${doc.externalProvider}${doc.externalPath ? ` · ${doc.externalPath}` : ''}`);
    if (doc.description) parts.push(doc.description);
    return parts.join('\n');
  }

  function projectDocumentAiContextReference(project, doc, targetLabel) {
    return {
      type: 'Project document',
      id: doc.id,
      label: doc.fileName || 'Untitled document',
      url: doc.externalUrl || `${window.location.origin}${window.location.pathname}#/projects/${encodeURIComponent(project.id)}/context`,
      fields: {
        Project: project.name || '',
        ProjectId: project.id || '',
        DocumentId: doc.id || '',
        Target: targetLabel || '',
        Description: doc.description || '',
        ContentType: doc.contentType || '',
        Source: doc.externalProvider || 'Local file',
        ExternalFileId: doc.externalFileId || '',
        ExternalPath: doc.externalPath || '',
        UploadedAt: doc.uploadedAt || '',
        Requirement: doc.isRequirement ? 'Yes' : '',
      },
    };
  }

  function editProjectDocumentDescription(project, onAction, doc) {
    const description = window.prompt('Document description', doc.description || '');
    if (description === null) return;
    onAction('updateProjectDocumentDescription', { projectId: project.id, documentId: doc.id, description });
  }

  function isFileDrag(e) {
    return Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');
  }

  function isStorageDocumentDrag(e) {
    return Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('application/x-appligator-storage-document-id');
  }

  function isProjectDocumentDrag(e) {
    return Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('application/x-appligator-document-id');
  }

  function uploadProjectDocumentFiles(project, onAction, target, files) {
    if (!project || !onAction || !files || !files.length) return;
    Array.from(files).forEach((file) => {
      onAction('uploadProjectDocument', { projectId: project.id, file, ...(target || {}) });
    });
  }

  function attachExternalProjectDocument(project, onAction, target) {
    const externalUrl = window.prompt('External document URL');
    if (externalUrl === null) return;
    const cleanUrl = externalUrl.trim();
    if (!cleanUrl) return;
    let fileName = '';
    try {
      const parsed = new URL(cleanUrl);
      fileName = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() || '');
    } catch (_) {
      fileName = '';
    }

    fileName = window.prompt('Document name', fileName || 'External document');
    if (fileName === null || !fileName.trim()) return;
    const provider = window.prompt('Storage provider', 'External');
    if (provider === null || !provider.trim()) return;
    const externalPath = window.prompt('External path or note', '');
    if (externalPath === null) return;
    onAction('attachExternalProjectDocument', {
      projectId: project.id,
      fileName: fileName.trim(),
      provider: provider.trim(),
      externalUrl: cleanUrl,
      externalPath: externalPath.trim(),
      ...(target || {}),
    });
  }

  function projectDocumentDropProps(project, onAction, target, setOver) {
    return {
      onDragOver: (e) => {
        const storageDocumentDrag = isStorageDocumentDrag(e);
        const projectDocumentDrag = isProjectDocumentDrag(e);
        if (!storageDocumentDrag && !projectDocumentDrag && !isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = storageDocumentDrag || projectDocumentDrag ? 'move' : 'copy';
        if (setOver) setOver(true);
      },
      onDragLeave: (e) => {
        if (!isStorageDocumentDrag(e) && !isProjectDocumentDrag(e) && !isFileDrag(e)) return;
        if (!e.currentTarget.contains(e.relatedTarget) && setOver) setOver(false);
      },
      onDrop: (e) => {
        const storageDocumentId = e.dataTransfer && e.dataTransfer.getData('application/x-appligator-storage-document-id');
        if (storageDocumentId) {
          e.preventDefault();
          e.stopPropagation();
          if (setOver) setOver(false);
          onAction('linkStorageDocumentToProject', { storageDocumentId, projectId: project.id, ...(target || {}) });
          return;
        }
        const documentId = e.dataTransfer && e.dataTransfer.getData('application/x-appligator-document-id');
        if (documentId) {
          e.preventDefault();
          e.stopPropagation();
          if (setOver) setOver(false);
          onAction('attachExistingProjectDocument', { documentId, projectId: project.id, ...(target || {}) });
          return;
        }
        const files = e.dataTransfer && e.dataTransfer.files;
        if (!files || !files.length) return;
        e.preventDefault();
        e.stopPropagation();
        if (setOver) setOver(false);
        uploadProjectDocumentFiles(project, onAction, target, files);
      },
    };
  }

  function ProjectDocumentArea({ project, target, onAction, empty = 'No documents attached.', compact = false, db, onToast, onError }) {
    const [over, setOver] = useState(false);
    const docs = projectDocumentsFor(project, target);
    const targetLabel = target && target.ticketId ? 'Ticket'
      : target && target.milestoneId ? 'Milestone'
        : target && target.workPackageId ? window.AC.projectItemLabel(project)
          : 'Project base';
    const upload = (e) => {
      const files = e.target.files;
      e.target.value = '';
      uploadProjectDocumentFiles(project, onAction, target, files);
    };
    const row = (doc, contextMenuProps) => (
      <div key={doc.id} className="pd-docarea-row" {...contextMenuProps(projectDocumentAiContextReference(project, doc, targetLabel))}>
        <button type="button" className="chip pd-docchip" title={projectDocumentTitle(doc)} onClick={() => onAction('openProjectDocument', { projectId: project.id, documentId: doc.id })}>
          <Icon name={doc.externalProvider ? "link" : "doc"} size={12} />{doc.fileName}
        </button>
        {doc.description ? <span className="pd-docdesc" title={doc.description}>{doc.description}</span> : null}
        <label className="checkrow" title="Requirement" style={{ flex: 'none' }}>
          <input type="checkbox" checked={!!doc.isRequirement} onChange={(e) => onAction('updateProjectDocumentRequirement', { projectId: project.id, documentId: doc.id, isRequirement: e.target.checked })} />Req
        </label>
        <button type="button" className="icon-btn" title="Edit description" onClick={() => editProjectDocumentDescription(project, onAction, doc)}>
          <Icon name="edit" size={13} />
        </button>
        <button type="button" className="icon-btn" title="Remove document" onClick={() => onAction('deleteProjectDocument', { projectId: project.id, documentId: doc.id, fileName: doc.fileName })}>
          <Icon name="trash" size={13} />
        </button>
      </div>
    );
    return (
      <AiEmployeeContextActions db={db} onToast={onToast} onError={onError}>
        {({ contextMenuProps }) => (
          <div className={'pd-docarea' + (compact ? ' compact' : '') + (over ? ' over' : '')} {...projectDocumentDropProps(project, onAction, target, setOver)}>
            <div className="pd-docarea-head">
              <label>Documents</label>
              <div className="actions">
                <button type="button" className="btn sm ghost" onClick={() => attachExternalProjectDocument(project, onAction, target)}><Icon name="link" size={13} />Link</button>
                <label className="btn sm ghost" style={{ cursor: 'pointer' }}><Icon name="plus" size={13} />Upload<input type="file" multiple style={{ display: 'none' }} onChange={upload} /></label>
              </div>
            </div>
            <div className="pd-docarea-list">
              {docs.length ? docs.map((doc) => row(doc, contextMenuProps)) : <span className="muted" style={{ fontSize: 12 }}>{empty}</span>}
            </div>
            <div className="pd-docarea-drop"><Icon name="download" size={13} />Drop files here</div>
          </div>
        )}
      </AiEmployeeContextActions>
    );
  }

  /* ---------- dashboard cards ---------- */
  function OverviewCard({ project, saveProject }) {
    const githubAccessUrl = window.AC.githubCodexAccessUrl(project.projectRepoUrl);
    const showSoftwareFields = window.AC.projectHasSoftwareFields(project);
    return (
      <Panel title="Overview">
        <div className="pd-meta-row" style={{ marginBottom: 12 }}>
          <StatusPick value={project.status} options={['Active', 'Completed', 'Inactive']} onChange={(v) => saveProject({ status: v })} />
          <span className="chip"><Inline value={project.kind} onCommit={(v) => saveProject({ kind: v })} /></span>
        </div>
        <Inline className="pd-overview-desc" multiline rows={5} value={project.description} onCommit={(v) => saveProject({ description: v })} placeholder="Add a project description…" style={{ display: 'block' }} />
        {showSoftwareFields && project.projectRepoUrl && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 13 }}>
          <a href={project.projectRepoUrl} target="_blank" rel="noreferrer" className="chip"><Icon name="external" size={12} />Project repo</a>
          {githubAccessUrl && <a href={githubAccessUrl} target="_blank" rel="noreferrer" className="chip" title="Grant ChatGPT Codex Connector access to this repository"><Icon name="shield" size={12} />GitHub access</a>}
        </div>}
        {showSoftwareFields && project.projectLocalRepoPath && <div style={{ marginTop: project.projectRepoUrl ? 8 : 13 }}><span className="chip"><Icon name="server" size={12} />Local repo: {project.projectLocalRepoPath}</span></div>}
        {showSoftwareFields && project.projectAppUrl && <div style={{ marginTop: (project.projectRepoUrl || project.projectLocalRepoPath) ? 8 : 13 }}><a href={project.projectAppUrl} target="_blank" rel="noreferrer" className="chip"><Icon name="external" size={12} />Deployed app</a></div>}
        {showSoftwareFields && project.designMdKitIdentifier && <div style={{ marginTop: 8 }}><a href={project.designMdKitUrl || `https://designmd.ai/${project.designMdKitIdentifier}`} target="_blank" rel="noreferrer" className="chip"><Icon name="doc" size={12} />{project.designMdKitName || project.designMdKitIdentifier}</a></div>}
      </Panel>
    );
  }

  function CustomersCard({ project, db, onDrawer, onAction }) {
    const custs = project.customerIds.map((id) => db.customers.find((c) => c.id === id)).filter(Boolean);
    const canLink = db.customers.some((c) => !project.customerIds.includes(c.id));
    return (
      <Panel title="Linked customers" sub={`${custs.length} linked`} actions={canLink && <button className="btn sm ghost" onClick={() => onDrawer('projectCustomer', null, { projectId: project.id })}><Icon name="plus" size={14} />Customer</button>}>
        {custs.length ? custs.map((c) => {
          const owe = customerOutstanding(db, c.id);
          return (
            <div className="pd-customer-row" key={c.id}>
              <Avatar name={c.name} />
              <div className="meta"><div className="nm">{c.name}</div><div className="em">{c.email}</div></div>
              {owe > 0 ? <span className="owe">{money(owe)} due</span> : <span className="faint" style={{ fontSize: 11 }}>Settled</span>}
              <button className="icon-btn" title="Remove customer link" onClick={() => onAction('unlinkProjectCustomer', { projectId: project.id, customerId: c.id, customerName: c.name })}><Icon name="trash" size={14} /></button>
            </div>
          );
        }) : <span className="muted" style={{ fontSize: 12 }}>No customers linked.</span>}
      </Panel>
    );
  }

  const STAGE_STYLES = [
    { accent: '#64748b', soft: 'var(--surface-3)' },
    { accent: 'var(--info)', soft: '#e2f1f4' },
    { accent: 'var(--primary)', soft: 'var(--primary-soft)' },
    { accent: '#6d4bd0', soft: '#eee9fb' },
    { accent: 'var(--warn)', soft: 'var(--warn-soft)' },
    { accent: 'var(--pos)', soft: 'var(--pos-soft)' },
  ];
  const projectPhases = (project) => MODEL.projectWorkPackagePhases ? MODEL.projectWorkPackagePhases(project) : (MODEL.WORK_PACKAGE_PHASES || []);
  const phaseIndexOf = (phase, phases, project) => {
    const normalized = window.AC.cleanProjectPhase ? window.AC.cleanProjectPhase(phase, project) : phase;
    return Math.max(0, (phases || MODEL.WORK_PACKAGE_PHASES || []).findIndex((item) => item.toLowerCase() === String(normalized || '').trim().toLowerCase()));
  };
  const phasePct = (index, phases) => Math.round((index / Math.max(1, (phases || MODEL.WORK_PACKAGE_PHASES || []).length - 1)) * 100);
  const initialsForName = (name) => String(name || 'Work package').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || 'F';
  const relativeChanged = (value) => {
    if (!value) return 'not moved';
    const days = Math.max(0, Math.round((new Date() - new Date(value)) / 86400000));
    if (days === 0) return 'today';
    if (days === 1) return '1d ago';
    if (days < 30) return `${days}d ago`;
    return fmtDate(value);
  };

  function WorkPackagePhaseStepper({ project, workPackage, onAction, compact }) {
    const phases = projectPhases(project);
    const phaseIndex = phaseIndexOf(workPackage.phase, phases, project);
    return (
      <div className={'pd-phase-stepper' + (compact ? ' compact' : '')}>
        {phases.map((phase, index) => (
          <button
            key={phase}
            type="button"
            className={'pd-phase-step' + (index < phaseIndex ? ' done' : index === phaseIndex ? ' on' : '')}
            title={`Set ${workPackage.name} to ${phase}`}
            onClick={() => onAction('updateWorkPackagePhase', { projectId: project.id, workPackageId: workPackage.id, phase })}
          >
            {phase}
          </button>
        ))}
      </div>
    );
  }

  function WorkPackagePipelinePanel({ project, onAction }) {
    const [draftName, setDraftName] = useState('');
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverPhase, setDragOverPhase] = useState(null);
    const [phaseOverrides, setPhaseOverrides] = useState({});
    const [changedOverrides, setChangedOverrides] = useState({});
    const phases = projectPhases(project);
    const itemLabel = window.AC.projectItemLabel(project);
    const baseSummary = MODEL.projectPhaseSummary(project);
    const workPackages = baseSummary.workPackages.map((workPackage) => {
      const phase = phaseOverrides[workPackage.id] || workPackage.phase;
      const phaseIndex = phaseIndexOf(phase, phases, project);
      return { ...workPackage, phase, phaseIndex, changedLabel: changedOverrides[workPackage.id] || relativeChanged(workPackage.phaseChangedAt) };
    });
    const total = workPackages.length;
    const done = workPackages.filter((workPackage) => workPackage.phase === 'Done').length;
    const maxProgress = total * Math.max(1, phases.length - 1);
    const overallPct = maxProgress ? Math.round((workPackages.reduce((sum, workPackage) => sum + workPackage.phaseIndex, 0) / maxProgress) * 100) : 0;
    const byPhase = phases.map((phase) => ({
      phase,
      workPackages: workPackages.filter((workPackage) => String(workPackage.phase || '').toLowerCase() === String(phase).toLowerCase()),
    }));
    const addWorkPackage = (e) => {
      e.preventDefault();
      const name = draftName.trim();
      if (!name) return;
      onAction('createProjectWorkPackage', { projectId: project.id, workPackage: { name } });
      setDraftName('');
    };
    const setWorkPackagePhase = (workPackage, phase) => {
      if (!phase || workPackage.phase === phase) return;
      setPhaseOverrides((current) => ({ ...current, [workPackage.id]: phase }));
      setChangedOverrides((current) => ({ ...current, [workPackage.id]: 'just now' }));
      onAction('updateWorkPackagePhase', { projectId: project.id, workPackageId: workPackage.id, phase });
    };
    const movePhase = (workPackage, direction) => {
      setWorkPackagePhase(workPackage, phases[workPackage.phaseIndex + direction]);
    };
    const dropOnPhase = (phase) => {
      const workPackage = workPackages.find((item) => item.id === draggingId);
      setDragOverPhase(null);
      setDraggingId(null);
      if (!workPackage) return;
      setWorkPackagePhase(workPackage, phase);
    };
    return (
      <Panel
        title={`${itemLabel} pipeline`}
        sub={`${done}/${total} ${itemLabel.toLowerCase()}${total === 1 ? '' : 's'} done · ${overallPct}% overall · drag cards between stages or use the arrows`}
        className="pd-pipeline-panel"
        actions={
          <>
            <div className="pd-overall-meter">
              <span>Overall</span>
              <b><i style={{ width: `${overallPct}%` }} /></b>
              <strong>{overallPct}%</strong>
            </div>
            <form className="pd-pipeline-add" onSubmit={addWorkPackage}>
              <input className="inp sm" placeholder={`New ${itemLabel.toLowerCase()}`} value={draftName} onChange={(e) => setDraftName(e.target.value)} />
              <button className="btn sm ghost" type="submit"><Icon name="plus" size={13} />Add</button>
            </form>
          </>
        }>
        <div className="pd-pipeline" style={{ '--pd-pipeline-cols': Math.max(1, byPhase.length) }}>
          {byPhase.map((col, index) => {
            const stageStyle = STAGE_STYLES[index] || STAGE_STYLES[0];
            return (
              <div
                className={'pd-pipeline-col' + (dragOverPhase === col.phase ? ' over' : '')}
                key={col.phase}
                onDragEnter={(e) => { e.preventDefault(); setDragOverPhase(col.phase); }}
                onDragOver={(e) => { e.preventDefault(); setDragOverPhase(col.phase); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverPhase(null); }}
                onDrop={(e) => { e.preventDefault(); dropOnPhase(col.phase); }}
              >
                <div className="pd-pipeline-stage" style={{ '--stage-accent': stageStyle.accent, '--stage-soft': stageStyle.soft }}>
                  <div />
                  <span>{col.phase}</span>
                  <b>{col.workPackages.length}</b>
                </div>
                {col.workPackages.length ? col.workPackages.map((workPackage) => {
                  const pct = phasePct(workPackage.phaseIndex, phases);
                  return (
                    <div
                      className="pd-pipeline-card"
                      key={workPackage.id}
                      draggable
                      title="Drag to another stage"
                      style={{ '--stage-accent': stageStyle.accent }}
                      onDragStart={(e) => {
                        setDraggingId(workPackage.id);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', workPackage.id);
                      }}
                      onDragEnd={() => { setDraggingId(null); setDragOverPhase(null); }}
                    >
                      <span className="pd-pipeline-spine" />
                      <strong>{workPackage.name}</strong>
                      <div className="pd-pipeline-progress">
                        <div><span>{pct}%</span><em>Step {workPackage.phaseIndex + 1} / {phases.length}</em></div>
                        <b><i style={{ width: `${pct}%` }} /></b>
                      </div>
                      <div className="pd-pipeline-card-actions">
                        <span className="pd-pipeline-avatar">{initialsForName(workPackage.name)}</span>
                        <span className="pd-pipeline-changed">{workPackage.changedLabel}</span>
                        <div />
                        {workPackages.length > 1 && <button className="icon-btn" title={`Remove ${workPackage.name}`} onClick={() => onAction('deleteProjectWorkPackage', { projectId: project.id, workPackageId: workPackage.id, name: workPackage.name })}><Icon name="trash" size={13} /></button>}
                        {workPackage.phaseIndex > 0 && <button className="icon-btn" title="Move back a phase" onClick={() => movePhase(workPackage, -1)}><Icon name="chevL" size={13} /></button>}
                        {workPackage.phaseIndex < phases.length - 1 && <button className="icon-btn primary" title="Advance a phase" onClick={() => movePhase(workPackage, 1)}><Icon name="chevR" size={13} /></button>}
                      </div>
                    </div>
                  );
                }) : <div className="pd-pipeline-empty">Empty</div>}
              </div>
            );
          })}
        </div>
      </Panel>
    );
  }

  function ProjectActionCenter({ project, db, onSelectTab }) {
    const showSoftwareFields = window.AC.projectHasSoftwareFields(project);
    const todayValue = (MODEL && MODEL.TODAY) || window.AC.TODAY;
    const now = new Date(todayValue + 'T00:00:00');
    const daysUntil = (iso) => Math.ceil((new Date(String(iso).slice(0, 10) + 'T00:00:00') - now) / 86400000);
    const followups = MODEL.openMeetingFollowups(db)
      .filter((item) => (item.meeting && item.meeting.projectId === project.id) || (item.event && item.event.projectId === project.id));
    const requirements = MODEL.projectRequirements(project);
    const items = [];
    (project.milestones || []).filter((m) => !m.completed && m.dueDate && String(m.dueDate) < todayValue).slice(0, 3).forEach((m) => {
      items.push({ key: `overdue-${m.id}`, tone: 'neg', icon: 'clock', title: m.title, meta: `Milestone overdue · ${fmtDate(m.dueDate)}`, tab: 'context' });
    });
    followups.slice(0, 3).forEach((item) => {
      items.push({ key: item.key, tone: 'warn', icon: item.type === 'Question' ? 'alert' : 'calendar', title: item.text, meta: `${item.type || 'Follow-up'} · ${item.meeting.title || 'Meeting'}`, tab: 'context' });
    });
    if (showSoftwareFields) {
      requirements.filter((item) => item.status === 'open question').slice(0, 3).forEach((item) => {
        items.push({ key: `req-${item.id}`, tone: 'warn', icon: 'flag', title: item.title, meta: 'Requirement has an open question', tab: 'design' });
      });
    }
    (project.milestones || []).filter((m) => !m.completed && m.dueDate && daysUntil(m.dueDate) >= 0 && daysUntil(m.dueDate) <= 10).slice(0, 3).forEach((m) => {
      items.push({ key: `due-${m.id}`, tone: 'primary', icon: 'clock', title: m.title, meta: `Due ${fmtDate(m.dueDate)}`, tab: 'context' });
    });
    const visible = items.slice(0, 6);
    return (
      <Panel title="Action center" sub={`${visible.length} open item${visible.length === 1 ? '' : 's'}`}>
        <div className="pd-action-list">
          {visible.length ? visible.map((item) => (
            <button className="pd-action-row" key={item.key} onClick={() => onSelectTab(item.tab)}>
              <span className={'pd-action-ico ' + item.tone}><Icon name={item.icon} size={13} /></span>
              <span><strong>{item.title}</strong><small>{item.meta}</small></span>
              <Icon name="chevR" size={13} />
            </button>
          )) : <div className="pd-emptyline"><Icon name="check" size={14} />All clear.</div>}
        </div>
      </Panel>
    );
  }

  function CompactOverviewPanel({ project, db, saveProject, onDrawer, onAction }) {
    const custs = project.customerIds.map((id) => db.customers.find((c) => c.id === id)).filter(Boolean);
    const canLink = db.customers.some((c) => !project.customerIds.includes(c.id));
    return (
      <Panel title="Overview" actions={<StatusPick value={project.status} options={['Active', 'Completed', 'Inactive']} onChange={(v) => saveProject({ status: v })} />}>
        <Inline className="pd-overview-desc clamped" multiline rows={5} value={project.description} onCommit={(v) => saveProject({ description: v })} placeholder="Add a project description…" style={{ display: 'block' }} />
        <div className="pd-overview-customers">
          <span>{custs.length} linked customer{custs.length === 1 ? '' : 's'}</span>
          {canLink && <button className="btn sm ghost" onClick={() => onDrawer('projectCustomer', null, { projectId: project.id })}><Icon name="plus" size={13} />Link</button>}
        </div>
        <div className="pd-compact-customers">
          {custs.length ? custs.map((c) => {
            const owe = customerOutstanding(db, c.id);
            return (
              <div className="pd-customer-row compact" key={c.id}>
                <Avatar name={c.name} />
                <div className="meta"><div className="nm">{c.name}</div></div>
                {owe > 0 ? <span className="owe">{money(owe)} due</span> : <span className="faint" style={{ fontSize: 11 }}>Settled</span>}
                <button className="icon-btn" title="Remove customer link" onClick={() => onAction('unlinkProjectCustomer', { projectId: project.id, customerId: c.id, customerName: c.name })}><Icon name="trash" size={13} /></button>
              </div>
            );
          }) : <span className="muted" style={{ fontSize: 12 }}>No customers linked.</span>}
        </div>
      </Panel>
    );
  }

  /* ---------- Documents (collapsible) ---------- */
  function DocumentsPanel({ project, db, onAction, onToast, onError }) {
    const [open, setOpen] = useState(false);
    const [targetValue, setTargetValue] = useState('project');
    const sections = project.workPackages || [];
    const itemLabel = window.AC.projectItemLabel(project);
    const milestones = project.milestones || [];
    const tickets = sections.flatMap((section) => (section.ticketClusters || []).flatMap((cluster) => (cluster.tickets || []).map((ticket) => ({ ...ticket, section, cluster }))));
    const total = (project.documents || []).length;
    const uploadTarget = () => {
      const [kind, id] = targetValue.split(':');
      return {
        workPackageId: kind === 'workPackage' ? id : null,
        milestoneId: kind === 'milestone' ? id : null,
        ticketId: kind === 'ticket' ? id : null,
      };
    };
    const targetLabel = (doc) => {
      if (doc.ticketId) {
        const ticket = tickets.find((item) => item.id === doc.ticketId);
        return ticket ? `Ticket · ${ticket.title}` : 'Ticket';
      }
      if (doc.milestoneId) {
        const milestone = milestones.find((item) => item.id === doc.milestoneId);
        return milestone ? `Milestone · ${milestone.title}` : 'Milestone';
      }
      if (doc.workPackageId) {
        const section = sections.find((item) => item.id === doc.workPackageId);
        return section ? `${itemLabel} · ${section.name}` : itemLabel;
      }
      return 'Project base';
    };
    const uploadProject = (e) => {
      const files = e.target.files;
      e.target.value = '';
      uploadProjectDocumentFiles(project, onAction, uploadTarget(), files);
    };
    const docRow = (doc, contextMenuProps) => {
      const label = targetLabel(doc);
      return (
      <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }} {...contextMenuProps(projectDocumentAiContextReference(project, doc, label))}>
        <span className="chip" title={projectDocumentTitle(doc)} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}><Icon name={doc.externalProvider ? "link" : "doc"} size={12} />{doc.fileName}</span>
        {doc.description ? <span className="pd-docdesc" title={doc.description}>{doc.description}</span> : null}
        {doc.externalProvider ? <span className="chip" title={doc.externalPath || doc.externalUrl}>{doc.externalProvider}</span> : null}
        <span className="chip" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <label className="checkrow" title="Requirement" style={{ flex: 'none' }}>
          <input type="checkbox" checked={!!doc.isRequirement} onChange={(e) => onAction('updateProjectDocumentRequirement', { projectId: project.id, documentId: doc.id, isRequirement: e.target.checked })} />Req
        </label>
        <button className="icon-btn" title="Edit description" onClick={() => editProjectDocumentDescription(project, onAction, doc)}><Icon name="edit" size={14} /></button>
        <button className="icon-btn" title="Open document" onClick={() => onAction('openProjectDocument', { projectId: project.id, documentId: doc.id })}><Icon name="external" size={14} /></button>
        <button className="icon-btn" title="Remove document" onClick={() => onAction('deleteProjectDocument', { projectId: project.id, documentId: doc.id, fileName: doc.fileName })}><Icon name="trash" size={14} /></button>
      </div>
      );
    };
    return (
      <AiEmployeeContextActions db={db} onToast={onToast} onError={onError}>
        {({ contextMenuProps }) => (
          <section className="panel">
            <div className="panel-head pd-panelhead-btn" onClick={() => setOpen((o) => !o)}>
              <Icon name="chevR" size={14} className={'pd-chev' + (open ? ' open' : '')} style={{ color: 'var(--faint)', flex: 'none' }} />
              <div><h2>Documents</h2><div className="sub">{total} file{total !== 1 ? 's' : ''}</div></div>
              <div className="actions" onClick={(e) => e.stopPropagation()}>
                <select className="input sm" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} style={{ width: 210 }}>
                  <option value="project">Project base</option>
                  {sections.map((section) => <option key={section.id} value={`workPackage:${section.id}`}>{itemLabel} · {section.name}</option>)}
                  {milestones.map((milestone) => <option key={milestone.id} value={`milestone:${milestone.id}`}>Milestone · {milestone.title}</option>)}
                  {tickets.map((ticket) => <option key={ticket.id} value={`ticket:${ticket.id}`}>Ticket · {ticket.title}</option>)}
                </select>
                <button type="button" className="btn sm ghost" onClick={() => attachExternalProjectDocument(project, onAction, uploadTarget())}><Icon name="link" size={13} />Link</button>
                <label className="btn sm ghost" style={{ cursor: 'pointer' }}><Icon name="plus" size={13} />Upload<input type="file" multiple style={{ display: 'none' }} onChange={uploadProject} /></label>
              </div>
            </div>
            {open && (
              <div className="panel-body">
                <div style={{ display: 'grid', gap: 7 }}>{(project.documents || []).length ? project.documents.map((doc) => docRow(doc, contextMenuProps)) : <span className="muted" style={{ fontSize: 12 }}>No project documents.</span>}</div>
              </div>
            )}
          </section>
        )}
      </AiEmployeeContextActions>
    );
  }

  /* ---------- Financials tab (tables identical to original ProjectDetail) ---------- */
  function FinancialsTab({ project, db, fin, onDrawer, onAction }) {
    return (
      <div className="grid">
        <div className="fin-summary">
          <KPI label="Total billed" icon="invoices" value={money(fin.billed)} delta={`${fin.invs.length} invoice${fin.invs.length !== 1 ? 's' : ''}`} />
          <KPI label="Outstanding" icon="wallet" tone={fin.outstanding > 0 ? 'warn' : 'pos'} value={fin.outstanding > 0 ? money(fin.outstanding) : '—'} delta={fin.outstanding > 0 ? 'Awaiting payment' : 'All settled'} />
          <KPI label="Project spend" icon="expenses" tone={fin.spend > 0 ? 'warn' : ''} value={fin.spend > 0 ? money(fin.spend) : '—'} delta={`${fin.expenses.length} expense${fin.expenses.length !== 1 ? 's' : ''}`} />
          <KPI label="Net after spend" icon="trendUp" tone={fin.net >= 0 ? 'pos' : 'neg'} value={money(fin.net)} delta="Billed minus project expenses" />
          <KPI label="Deliverables & offers" icon="projects" value={`${project.projectDeliverables.length} · ${(project.offers || []).length}`} delta="Active scope" />
        </div>

        <Panel title="Deliverables & offers" sub={`${project.projectDeliverables.length} deliverable${project.projectDeliverables.length !== 1 ? 's' : ''} · ${(project.offers || []).length} offer${(project.offers || []).length !== 1 ? 's' : ''}`}
          actions={<div style={{ display: 'flex', gap: 6 }}><button className="btn sm ghost" onClick={() => onDrawer('projectOffer', null, { projectId: project.id })}><Icon name="doc" size={14} />Offer</button><button className="btn sm ghost" onClick={() => onDrawer('projectDeliverable', null, { projectId: project.id })}><Icon name="plus" size={14} />Deliverable</button></div>} flush>
          <DataTable
            rows={projectDeliverableRows(project, db)} rowKey={(row) => row.id} empty="No deliverables or offers yet."
            columns={[
              { header: 'Item', render: (row) => row.type === 'deliverable' ? <div className="cellstack"><span className="t-strong">{row.deliverable.name}</span><span className="t-sub">Deliverable</span></div> : <div className="cellstack"><span className="t-strong">{row.offer.title}</span><span className="t-sub">Offer · {(row.offer.lines || []).length} line{(row.offer.lines || []).length !== 1 ? 's' : ''}</span></div> },
              { header: 'Type', className: 'muted', render: (row) => row.type === 'deliverable' ? row.deliverable.kind : 'Offer' },
              { header: 'Status', render: (row) => row.type === 'deliverable' ? <Pill kind={row.deliverable.status === 'Closed' ? 'Inactive' : row.deliverable.status || 'Active'} sm /> : <Pill kind={row.offer.status || 'Offered'} sm /> },
              { header: 'Billing', render: (row) => row.type === 'deliverable' ? <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{row.deliverable.freeOfCharge ? <span className="chip">Free of charge</span> : !row.milestoneBillable ? <span className="chip">Milestone open</span> : (row.deliverable.recurrenceFrequency && row.deliverable.recurrenceFrequency !== 'None') ? <span className="chip"><Icon name="refresh" size={11} />{row.deliverable.recurrenceFrequency}</span> : <span className="faint" style={{ whiteSpace: 'nowrap' }}>One-off</span>}{row.missingInvoiceCount > 0 && <span className="chip" style={{ background: 'var(--warn-soft)', color: 'var(--warn)', borderColor: '#f0d9b8' }} title="Invoices need to be created"><Icon name="invoices" size={11} />{row.missingInvoiceCount} due</span>}</div> : '—' },
              { header: 'Amount', className: 'num', render: (row) => row.type === 'deliverable' ? money(row.deliverable.unitPrice) : money(offerTotals(row.offer).total) },
              { header: 'Tax', className: 'num muted', render: (row) => row.type === 'deliverable' ? percent(row.deliverable.taxRate) : '—' },
            ]}
            actions={[
              { key: 'convert', label: 'Convert', show: (row) => row.type === 'offer' && row.offer.status !== 'Converted', onClick: (row) => onAction('convertProjectOffer', row.offer) },
              { key: 'download', title: 'Download PDF', icon: 'download', show: (row) => row.type === 'offer', onClick: (row) => onAction('downloadProjectOffer', row.offer) },
              { key: 'invoiceDeliverable', title: 'Create deliverable invoices', icon: 'invoices', show: (row) => row.type === 'deliverable' && !row.deliverable.freeOfCharge && row.milestoneBillable, onClick: (row) => onDrawer('deliverableInvoice', null, { projectId: project.id, deliverableId: row.deliverable.id }) },
              { key: 'editDeliverable', title: 'Edit deliverable', icon: 'edit', show: (row) => row.type === 'deliverable', onClick: (row) => onDrawer('projectDeliverable', row.deliverable.id, { projectId: project.id }) },
              { key: 'editOffer', title: 'Edit offer', icon: 'edit', show: (row) => row.type === 'offer' && row.offer.status !== 'Converted', onClick: (row) => onDrawer('projectOffer', row.offer.id, { projectId: project.id }) },
              { key: 'deleteDeliverable', title: 'Delete deliverable', icon: 'trash', show: (row) => row.type === 'deliverable', onClick: (row) => onAction('deleteProjectDeliverable', row.deliverable) },
              { key: 'deleteOffer', title: 'Delete offer', icon: 'trash', show: (row) => row.type === 'offer', onClick: (row) => onAction('deleteProjectOffer', row.offer) },
            ]} />
        </Panel>

        <Panel title="Expenses" sub={`${fin.expenses.length} linked to this project`}
          actions={<button className="btn sm ghost" onClick={() => onDrawer('expense', null, { projectId: project.id })}><Icon name="plus" size={14} />Record expense</button>} flush>
          <DataTable
            rows={fin.expenses.slice().sort((a, b) => b.date.localeCompare(a.date))} rowKey={(expense) => expense.id} empty="No expenses linked to this project."
            columns={[
              { header: 'Date', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (expense) => fmtDate(expense.date) },
              { header: 'Category', render: (expense) => <React.Fragment><span className="t-strong">{expense.category}</span>{expense.isWage && <span className="pill partial sm" style={{ marginLeft: 6 }}><span className="dot" />Wage</span>}</React.Fragment> },
              { header: 'Vendor', render: (expense) => expense.vendor || '—' },
              { header: 'Description', className: 'muted', cellStyle: { maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, key: 'description' },
              { header: 'Recurrence', render: (expense) => expense.recurrenceFrequency && expense.recurrenceFrequency !== 'None' ? <span className="chip"><Icon name="refresh" size={11} />{expense.recurrenceFrequency}</span> : <span className="faint">One-off</span> },
              { header: 'Total', className: 'num t-strong', render: (expense) => money((Number(expense.amount) || 0) + (Number(expense.taxAmount) || 0)) },
            ]}
            actions={[
              { key: 'doc', title: 'Document attached', icon: 'doc', show: (expense) => !!expense.document, onClick: (expense) => onAction('doc', expense) },
              { key: 'edit', title: 'Edit expense', icon: 'edit', onClick: (expense) => onDrawer('expense', expense.id, { projectId: project.id }) },
              { key: 'delete', title: 'Delete expense', icon: 'trash', show: (expense) => !(expense.payments || []).length, onClick: (expense) => onAction('deleteExpense', expense) },
            ]} />
        </Panel>

        <Panel title="Invoices" sub={`${fin.invs.length} for this project`} flush>
          <DataTable
            rows={fin.invs} rowKey={(iv) => iv.id} empty="No invoices yet."
            columns={[
              { header: 'Invoice', className: 'mono t-strong', render: (iv) => iv.number },
              { header: 'Issue', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (iv) => fmtDate(iv.issueDate) },
              { header: 'Status', render: (iv) => <Pill kind={MODEL.enrich(db, iv).display} sm /> },
              { header: 'Total', className: 'num', render: (iv) => money(MODEL.enrich(db, iv).totals.total) },
              { header: 'Outstanding', className: 'num', render: (iv) => { const e = MODEL.enrich(db, iv); return e.totals.outstanding > 0 ? money(e.totals.outstanding) : '—'; } },
            ]}
            actions={[
              { key: 'finalize', label: 'Finalize', show: (iv) => iv.status === 'Draft', onClick: (iv) => onAction('finalize', iv) },
              { key: 'pay', label: 'Mark paid', show: (iv) => MODEL.enrich(db, iv).open, onClick: (iv) => onAction('pay', iv) },
              { key: 'edit', title: 'Edit', icon: 'edit', show: (iv) => iv.status === 'Draft', onClick: (iv) => onDrawer('invoice', iv.id) },
              { key: 'delete', title: 'Delete invoice', icon: 'trash', show: (iv) => iv.status === 'Draft', onClick: (iv) => onAction('delete', iv) },
              { key: 'download', title: 'Download invoice', icon: 'download', onClick: (iv) => onAction('download', iv) },
              { key: 'open', title: 'Open invoice', icon: 'external', onClick: (iv) => onAction('print', iv) },
            ]} />
        </Panel>
      </div>
    );
  }

  /* ---------- Testing tab (external UI runner suites) ---------- */
  function ProjectTestingTab({ project, onAction }) {
    return <ProjectTestingSuiteTab project={project} onAction={onAction} />;
  }

  function ProjectTestingSuiteTab({ project, onAction }) {
    const [dashboard, setDashboard] = useState({ suites: [], environments: [], runs: [], runners: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [testingInstructionsDraft, setTestingInstructionsDraft] = useState(project.testingInstructions || '');
    const [testingInstructionsSaving, setTestingInstructionsSaving] = useState(false);
    const [runnerSetup, setRunnerSetup] = useState(null);
    const [selectedSuiteId, setSelectedSuiteId] = useState(null);
    const [runnerOpen, setRunnerOpen] = useState(false);
    const [expandedCases, setExpandedCases] = useState({});
    const [expandedRuns, setExpandedRuns] = useState({});
    const defaultEnvironmentDraft = () => ({
      id: null,
      name: 'Local',
      targetUrl: project.projectAppUrl || 'http://localhost:3000',
      startCommand: '',
      stopCommand: '',
      workingDirectory: project.projectLocalRepoPath || '',
      startupTimeoutSeconds: 60,
      showBrowser: false,
      slowMoMilliseconds: 250,
    });
    const [environmentDraft, setEnvironmentDraft] = useState(defaultEnvironmentDraft);

    const applyDashboard = React.useCallback((next) => {
      setDashboard(next || { suites: [], environments: [], runs: [], runners: [] });
      setError(null);
      if (selectedSuiteId && !(next.suites || []).some((suite) => suite.id === selectedSuiteId)) setSelectedSuiteId(null);
    }, [selectedSuiteId]);

    const load = React.useCallback(async () => {
      try {
        setLoading(true);
        applyDashboard(await window.API.getProjectTesting(project.id));
      } catch (e) {
        setError((e && e.message) || 'Failed to load test suites.');
      } finally {
        setLoading(false);
      }
    }, [project.id, applyDashboard]);

    const refresh = React.useCallback(async () => {
      try {
        const status = await window.API.getProjectTestingStatus(project.id);
        setDashboard((current) => ({
          ...current,
          runs: (status && status.runs) || current.runs || [],
          runners: (status && status.runners) || current.runners || [],
        }));
      } catch {
        /* keep the current table stable during background status refresh failures */
      }
    }, [project.id]);

    React.useEffect(() => { load(); }, [load]);
    React.useEffect(() => {
      setTestingInstructionsDraft(project.testingInstructions || '');
    }, [project.id, project.testingInstructions]);
    React.useEffect(() => {
      if (!window.API.subscribeToChanges) return undefined;
      let timer = null;
      const unsubscribe = window.API.subscribeToChanges((change) => {
        if (!change || change.toolName !== 'testing') return;
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          timer = null;
          refresh();
        }, 100);
      });
      return () => {
        if (timer) window.clearTimeout(timer);
        unsubscribe();
      };
    }, [refresh]);

    const suites = dashboard.suites || [];
    const environments = dashboard.environments || [];
    const runs = dashboard.runs || [];
    const runners = dashboard.runners || [];
    const onlineRunners = runners.filter((runner) => ['online', 'busy'].includes(String(runner.status || '').toLowerCase()));
    const companionRunner = runners.find((runner) => String(runner.name || '').toLowerCase() === 'appligator companion');
    const primaryEnv = environments[0] || null;
    const selectedSuite = suites.find((suite) => suite.id === selectedSuiteId) || suites[0] || null;
    const isActiveStatus = (status) => ['queued', 'running'].includes(String(status || '').toLowerCase());
    const activeRun = runs.find((run) => isActiveStatus(run.status)) || null;
    const activeSuite = activeRun ? suites.find((suite) => suite.id === activeRun.suiteId) : null;
    const testingInstructionsChanged = testingInstructionsDraft !== (project.testingInstructions || '');

    async function saveTestingInstructions(event) {
      event.preventDefault();
      if (!testingInstructionsChanged || !onAction) return;
      try {
        setTestingInstructionsSaving(true);
        const ok = await onAction('updateProject', { projectId: project.id, project, patch: { testingInstructions: testingInstructionsDraft } });
        if (ok === false) {
          setError('Failed to save testing instructions.');
        }
      } catch (e) {
        setError((e && e.message) || 'Failed to save testing instructions.');
      } finally {
        setTestingInstructionsSaving(false);
      }
    }

    async function generateSuite() {
      try {
        const suite = await window.API.generateProjectTestSuite(project.id, {});
        setSelectedSuiteId(suite.id);
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to generate suite.');
      }
    }

    async function removeSuite(suite) {
      if (!window.confirm(`Delete ${suite.name}?`)) return;
      try {
        await window.API.deleteProjectTestSuite(project.id, suite.id);
        if (selectedSuiteId === suite.id) setSelectedSuiteId(null);
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to delete suite.');
      }
    }

    async function saveEnvironment(event) {
      event.preventDefault();
      try {
        const payload = {
          name: environmentDraft.name,
          targetUrl: environmentDraft.targetUrl,
          authJson: JSON.stringify({
            startCommand: environmentDraft.startCommand || '',
            stopCommand: environmentDraft.stopCommand || '',
            workingDirectory: environmentDraft.workingDirectory || '',
            startupTimeoutSeconds: Number(environmentDraft.startupTimeoutSeconds) || 60,
            showBrowser: !!environmentDraft.showBrowser,
            slowMoMilliseconds: Number(environmentDraft.slowMoMilliseconds) || 0,
          }),
        };
        if (environmentDraft.id) await window.API.updateProjectTestEnvironment(project.id, environmentDraft.id, payload);
        else await window.API.createProjectTestEnvironment(project.id, payload);
        setEnvironmentDraft(defaultEnvironmentDraft());
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to save environment.');
      }
    }

    function editEnvironment(environment) {
      const settings = readJson(environment.authJson, {});
      setEnvironmentDraft({
        id: environment.id,
        name: environment.name || 'Local',
        targetUrl: environment.targetUrl || '',
        startCommand: settings.startCommand || '',
        stopCommand: settings.stopCommand || '',
        workingDirectory: settings.workingDirectory || project.projectLocalRepoPath || '',
        startupTimeoutSeconds: settings.startupTimeoutSeconds || 60,
        showBrowser: !!settings.showBrowser,
        slowMoMilliseconds: settings.slowMoMilliseconds || 250,
      });
      setRunnerOpen(true);
    }

    async function startRun(suite, environment) {
      try {
        const result = await window.API.startProjectTestRun(project.id, { suiteId: suite.id, environmentId: environment ? environment.id : null, targetUrl: environment ? null : (project.projectAppUrl || environmentDraft.targetUrl) });
        setNotice(result && result.message ? result.message : null);
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to start run.');
        await load();
      }
    }

    async function startQueuedRun(run) {
      try {
        const result = await window.API.startQueuedProjectTestRun(project.id, run.id);
        setNotice(result && result.message ? result.message : null);
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to start queued run.');
        await load();
      }
    }

    async function cancelRun(run) {
      if (!window.confirm(`Stop ${run.suiteName || 'this test run'}?`)) return;
      try {
        await window.API.cancelProjectTestRun(project.id, run.id);
        setNotice('Test run stopped.');
        await refresh();
      } catch (e) {
        setError((e && e.message) || 'Failed to stop test run.');
        await refresh();
      }
    }

    async function createRunnerSetup() {
      try {
        const setup = await window.API.createProjectTestRunnerSetup(project.id, { name: `${project.name || 'Project'} local runner` });
        setRunnerSetup(setup);
        setNotice('Local runner token created.');
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to create local runner setup.');
      }
    }

    async function downloadRunnerInstaller() {
      try {
        await window.API.downloadProjectTestRunnerInstaller(project.id, { name: `${project.name || 'Project'} local runner` });
        setNotice('Installer downloaded. Run it once, then this page will show the runner online.');
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to download local runner installer.');
      }
    }

    async function removeRunner(runner) {
      if (!window.confirm(`Remove ${runner.name || 'this runner'} from Appligator? This does not stop a local process that is still running.`)) return;
      try {
        await window.API.deleteProjectTestRunner(project.id, runner.id);
        setNotice('Runner removed from this project.');
        await load();
      } catch (e) {
        setError((e && e.message) || 'Failed to remove runner.');
      }
    }

    function readJson(value, fallback) {
      if (!value) return fallback;
      try { return JSON.parse(value); } catch { return fallback; }
    }

    function stepLabel(step) {
      if (typeof step === 'string') return step;
      if (String(step.action || '').toLowerCase() === 'drag') {
        const input = readJson(step.inputJson, {});
        if (input.target) return 'Drag to target';
        return `Drag by ${Number(input.deltaX) || 0}, ${Number(input.deltaY) || 0}`;
      }
      return step.label || step.description || step.title || step.name || step.action || 'Step';
    }

    function caseName(testCase) {
      return testCase.name || testCase.title || 'Case';
    }

    function flatSteps(suite) {
      return suite ? (suite.cases || []).flatMap((testCase) => (testCase.steps || []).map((step) => ({
        caseName: caseName(testCase),
        label: stepLabel(step),
      }))) : [];
    }

    function suiteStepCount(suite) {
      return (suite.cases || []).reduce((total, testCase) => total + ((testCase.steps || []).length), 0);
    }

    function dotsForSuite(suiteId) {
      return runs
        .filter((run) => run.suiteId === suiteId)
        .slice(0, 7)
        .reverse()
        .map((run) => {
          const status = String(run.status || '').toLowerCase();
          if (status === 'passed') return 'pass';
          if (status === 'failed') return 'fail';
          return 'warn';
        });
    }

    function latestForSuite(suiteId) {
      return runs.find((run) => run.suiteId === suiteId) || null;
    }

    function liveView(run, suite) {
      const steps = flatSteps(suite);
      const total = steps.length || (run.results || []).length || 0;
      const done = (run.results || []).length;
      const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (isActiveStatus(run.status) ? 12 : 100);
      const cursor = Math.min(done, Math.max(total - 1, 0));
      const start = Math.max(0, cursor - 5);
      const end = Math.min(total, cursor + 3);
      const visible = [];
      for (let i = start; i < end; i++) {
        const result = (run.results || [])[i];
        const current = isActiveStatus(run.status) && i === done;
        let cls = 'pending';
        if (result) cls = String(result.status || '').toLowerCase() === 'passed' ? 'pass' : 'fail';
        else if (current) cls = 'current';
        visible.push({ ...(steps[i] || { caseName: 'Run', label: `Step ${i + 1}` }), cls, current });
      }
      return { total, done, pct, visible };
    }

    function stringifyTriageItem(item) {
      return typeof item === 'string' ? item : JSON.stringify(item, null, 2);
    }

    function failureOf(run) {
      const failedResults = (run.results || []).filter((item) => String(item.status || '').toLowerCase() !== 'passed');
      const summary = readJson(run.summaryJson, {});
      if (!run.error && failedResults.length === 0 && !summary.exception) return null;
      const first = failedResults[0] || {};
      const details = readJson(first.detailsJson, {});
      const groups = [
        ['Console errors', details.consoleErrors],
        ['Page errors', details.pageErrors],
        ['Failed requests', details.failedRequests],
        ['Failed responses', details.failedResponses],
      ].filter(([, items]) => (items || []).length).map(([label, items]) => ({ label, items }));
      if (run.error) groups.unshift({ label: 'Runner error', items: [run.error] });
      if (summary.exception) groups.unshift({ label: 'Exception', items: [summary.exception] });
      return {
        stepLabel: first.message || first.status || 'Failure',
        url: details.url,
        groups,
      };
    }

    function toggleCase(caseId) {
      setExpandedCases((current) => ({ ...current, [caseId]: !current[caseId] }));
    }

    function toggleRun(runId) {
      setExpandedRuns((current) => ({ ...current, [runId]: !current[runId] }));
    }

    const passRate = (() => {
      const recent = runs.slice(0, 14);
      const passed = recent.filter((run) => String(run.status || '').toLowerCase() === 'passed').length;
      return recent.length ? Math.round((passed / recent.length) * 100) : 0;
    })();
    const totalCases = suites.reduce((total, suite) => total + (suite.cases || []).length, 0);
    const failingSuites = new Set(runs
      .filter((run) => String(run.status || '').toLowerCase() === 'failed')
      .slice(0, 8)
      .map((run) => run.suiteId)).size;
    const live = activeRun ? liveView(activeRun, activeSuite) : null;

    return (
      <div className="pd-test3">
        <div className="pd-test3-header">
          <div>
            <div className="crumb">{project.name} · Project</div>
            <h1>Testing</h1>
          </div>
          <div className="pd-test3-header-actions">
            <span className="chip">
              <span className="pd-test3-chip-dot" style={{ background: onlineRunners.length ? 'var(--pos)' : 'var(--faint)' }} />
              {onlineRunners.length ? (companionRunner ? 'Companion online' : `${onlineRunners.length} runner${onlineRunners.length !== 1 ? 's' : ''} online`) : 'No companion'}
              {primaryEnv ? ` · ${primaryEnv.name}` : ''}
            </span>
            <button className="btn ghost sm" onClick={() => setRunnerOpen((open) => !open)}>
              <Icon name="settings" size={13} />Runner &amp; environment
              <Icon name="chevD" size={12} style={{ transform: runnerOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </button>
          </div>
        </div>

        {runnerOpen && (
          <div className="pd-test3-drawer">
            <div>
              <div className="col-label">Appligator Companion</div>
              {runners.length ? runners.map((runner) => (
                <div className="pd-test3-runner-row" key={runner.id}>
                  <span className="dot" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{runner.name}</div>
                    <div className="machine">{runner.machine || 'Waiting for first heartbeat'}</div>
                  </div>
                  <Pill kind={runner.status || 'Pending'} sm />
                  {String(runner.name || '').toLowerCase() !== 'appligator companion' && <button className="icon-btn" title="Remove runner" onClick={() => removeRunner(runner)}><Icon name="trash" size={14} /></button>}
                </div>
              )) : <div className="pd-test3-empty">Appligator Companion is offline.</div>}
              {runnerSetup && <textarea className="inp mono" rows="2" readOnly value={runnerSetup.command || ''} />}
            </div>

            <div>
              <div className="pd-test3-env-title">
                <div className="col-label">Environment</div>
                {primaryEnv && <button className="btn ghost sm" type="button" onClick={() => editEnvironment(primaryEnv)}><Icon name="edit" size={12} />Edit {primaryEnv.name}</button>}
              </div>
              <form className="pd-test3-env" onSubmit={saveEnvironment}>
                <div className="row2">
                  <div className="field" style={{ margin: 0 }}><label>Name</label>
                    <input className="inp" value={environmentDraft.name} required onChange={(e) => setEnvironmentDraft((current) => ({ ...current, name: e.target.value }))} /></div>
                  <div className="field" style={{ margin: 0 }}><label>Target URL</label>
                    <input className="inp mono" value={environmentDraft.targetUrl} required onChange={(e) => setEnvironmentDraft((current) => ({ ...current, targetUrl: e.target.value }))} /></div>
                </div>
                <div className="field" style={{ margin: 0 }}><label>Start command <span className="opt">optional, boots the app before testing</span></label>
                  <input className="inp mono" placeholder="npm run dev -- --host 127.0.0.1" value={environmentDraft.startCommand} onChange={(e) => setEnvironmentDraft((current) => ({ ...current, startCommand: e.target.value }))} /></div>
                <div className="field" style={{ margin: 0 }}><label>Kill command <span className="opt">optional, runs after tests when this runner started the app</span></label>
                  <input className="inp mono" placeholder="lsof -ti tcp:3000 -sTCP:LISTEN | xargs kill" value={environmentDraft.stopCommand} onChange={(e) => setEnvironmentDraft((current) => ({ ...current, stopCommand: e.target.value }))} /></div>
                <div className="field" style={{ margin: 0 }}><label>Working directory</label>
                  <input className="inp mono" placeholder={project.projectLocalRepoPath || '/path/to/app'} value={environmentDraft.workingDirectory} onChange={(e) => setEnvironmentDraft((current) => ({ ...current, workingDirectory: e.target.value }))} /></div>
                <div className="row-half">
                  <div className="field" style={{ margin: 0 }}><label>Startup timeout (s)</label>
                    <input className="inp mono" type="number" min="5" max="180" value={environmentDraft.startupTimeoutSeconds} onChange={(e) => setEnvironmentDraft((current) => ({ ...current, startupTimeoutSeconds: e.target.value }))} /></div>
                  <div className="field" style={{ margin: 0 }}><label>Slow-motion (ms)</label>
                    <input className="inp mono" type="number" min="0" max="5000" value={environmentDraft.slowMoMilliseconds} onChange={(e) => setEnvironmentDraft((current) => ({ ...current, slowMoMilliseconds: e.target.value }))} /></div>
                </div>
                <label className="checkrow"><input type="checkbox" checked={!!environmentDraft.showBrowser} onChange={(e) => setEnvironmentDraft((current) => ({ ...current, showBrowser: e.target.checked }))} />Show browser window while the run executes</label>
                <div className="pd-test3-env-actions">
                  {environmentDraft.id && <button type="button" className="btn ghost" onClick={() => setEnvironmentDraft(defaultEnvironmentDraft())}>Cancel</button>}
                  <button className="btn" type="submit"><Icon name="check" size={14} />{environmentDraft.id ? 'Update environment' : 'Save environment'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="pd-test3-health">
          <div className="cell"><span className="k">Pass rate · recent</span><span className={'v ' + (passRate >= 85 ? 'pos' : passRate >= 60 ? '' : 'neg')}>{passRate}%</span></div>
          <div className="cell"><span className="k">Suites</span><span className="v">{suites.length}</span></div>
          <div className="cell"><span className="k">Test cases</span><span className="v">{totalCases}</span></div>
          <div className="cell"><span className="k">Failing suites</span><span className={'v ' + (failingSuites ? 'neg' : 'pos')}>{failingSuites}</span></div>
          <div className="cell now"><span className="k">Now running</span><span className={'v sm ' + (activeRun ? 'run' : '')}>{activeRun ? activeRun.suiteName : 'Idle'}</span></div>
        </div>

        {error && <div className="alert" style={{ margin: 12 }}>{error}</div>}
        {notice && <div className="alert" style={{ margin: 12 }}>{notice}</div>}

        <form className="pd-test3-instructions" onSubmit={saveTestingInstructions}>
          <div className="pd-test3-instructions-head">
            <div>
              <h2>Testing instructions</h2>
              <p>Shared with agents when they plan, implement, or run tests for this project.</p>
            </div>
            <div className="pd-test3-instructions-actions">
              {testingInstructionsChanged && <button className="btn ghost sm" type="button" onClick={() => setTestingInstructionsDraft(project.testingInstructions || '')}>Reset</button>}
              <button className="btn sm" type="submit" disabled={!testingInstructionsChanged || testingInstructionsSaving}>
                <Icon name="check" size={13} />{testingInstructionsSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          <textarea
            className="inp"
            rows="4"
            value={testingInstructionsDraft}
            onChange={(event) => setTestingInstructionsDraft(event.target.value)}
            placeholder="Auth flow, seeded accounts, feature flags, data setup, payment/test-mode caveats..."
          />
        </form>

        <div className="pd-test3-body">
          <div className="pd-test3-rail">
            <div className="pd-test3-rail-head">
              <span className="ttl">Suites</span>
              <span style={{ fontSize: 11, color: 'var(--faint)' }}>{suites.length} total</span>
              <button className="btn sm ghost" style={{ marginLeft: 'auto' }} onClick={generateSuite}><Icon name="plus" size={13} />Generate</button>
            </div>
            {loading ? <div className="pd-test3-empty">Loading...</div> : suites.length ? suites.map((suite) => {
              const latest = latestForSuite(suite.id);
              const running = activeRun && activeRun.suiteId === suite.id;
              return (
                <button key={suite.id} className={'pd-test3-suite' + (selectedSuite && suite.id === selectedSuite.id ? ' selected' : '')} onClick={() => setSelectedSuiteId(suite.id)}>
                  <span className="accent" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {running && <span className="running-dot" />}
                    <span className="nm">{suite.name}</span>
                    <Pill kind={suite.status || 'Draft'} sm />
                  </div>
                  <div className="cov">{(suite.cases || []).length} cases · {suiteStepCount(suite)} steps</div>
                  <div className="pd-test3-suite-recent">
                    <span className="label">Recent</span>
                    <span className="pd-test3-dots">{dotsForSuite(suite.id).map((dot, index) => <i key={index} className={dot} />)}</span>
                    <span className="latest">{latest ? fmtDate(String(latest.finishedAt || latest.startedAt || latest.queuedAt).slice(0, 10)) : '—'}</span>
                  </div>
                </button>
              );
            }) : <div className="pd-test3-empty">No UI test suites yet.</div>}
          </div>

          <div className="pd-test3-center">
            {activeRun && live ? (
              <div className={'pd-test3-monitor' + (String(activeRun.status || '').toLowerCase() === 'failed' ? ' failed' : '')}>
                <div className="head">
                  {isActiveStatus(activeRun.status) ? <span className="pd-test3-spinner" /> : <Icon name={String(activeRun.status || '').toLowerCase() === 'failed' ? 'close' : 'check'} size={16} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pd-test3-monitor-title">
                      <span className="label">{isActiveStatus(activeRun.status) ? 'Live run' : 'Run ' + String(activeRun.status || '').toLowerCase()}</span>
                      <span className="sep">·</span>
                      <span className="suite">{activeRun.suiteName}</span>
                    </div>
                    <div className="target">target: {activeRun.environmentName || activeRun.targetUrl}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}><div className="pct">{live.pct}%</div><div className="counts">{live.done} / {live.total} steps</div></div>
                  {isActiveStatus(activeRun.status) && <button className="icon-btn" title="Stop run" onClick={() => cancelRun(activeRun)}><Icon name="close" size={15} /></button>}
                </div>
                <div className="body">
                  <div className={'pd-test3-bar' + (isActiveStatus(activeRun.status) ? '' : ' done')}><span style={{ width: live.pct + '%' }} /></div>
                  <div className="pd-test3-step-eyebrow">{isActiveStatus(activeRun.status) ? 'Current step' : 'Last step'}</div>
                  <div className="pd-test3-steplist">
                    {live.visible.map((step, index) => (
                      <div key={index} className={'pd-test3-step ' + step.cls}>
                        <span className="state" style={{ color: step.cls === 'pass' ? 'var(--pos)' : step.cls === 'fail' ? 'var(--neg)' : step.cls === 'current' ? 'var(--primary-strong)' : 'var(--faint)' }}>
                          {step.cls === 'current' ? <span className="pd-test3-spinner" style={{ width: 10, height: 10 }} /> : step.cls === 'pass' ? <Icon name="check" size={13} /> : step.cls === 'fail' ? <Icon name="close" size={12} /> : <span className="pending-dot" />}
                        </span>
                        <span className="lbl">{step.label}</span>
                        <span className="case">{step.caseName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="pd-test3-empty">
                <div style={{ fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>No run in progress</div>
                {selectedSuite && <button className="btn" onClick={() => startRun(selectedSuite, primaryEnv)}><Icon name="check" size={13} />Run {selectedSuite.name}</button>}
              </div>
            )}

            <div className="pd-test3-panel">
              <div className="ph"><h2>Activity</h2><span className="sub">all suites · live feed</span></div>
              {runs.length ? runs.slice(0, 12).map((run) => {
                const failure = failureOf(run);
                const open = !!expandedRuns[run.id];
                return (
                  <div key={run.id}>
                    <div className={'pd-test3-feed-row' + (failure ? ' expandable' : '') + (open ? ' open' : '')} onClick={() => failure && toggleRun(run.id)}>
                      <div className="l1">
                        <Pill kind={run.status} sm />
                        <span className="nm">{run.suiteName}</span>
                        <span className="when">{fmtDate(String(run.queuedAt || run.startedAt || '').slice(0, 10))}</span>
                      </div>
                      <div className="l2">
                        <span className="meta">{(run.results || []).filter((result) => String(result.status || '').toLowerCase() === 'passed').length}/{(run.results || []).length} passed</span>
                        <span className="target">{run.environmentName || run.targetUrl}</span>
                        {failure && <Icon name="chevR" size={13} style={{ color: 'var(--neg)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />}
                        {isActiveStatus(run.status) && <button className="icon-btn" title="Stop" onClick={(event) => { event.stopPropagation(); cancelRun(run); }}><Icon name="close" size={14} /></button>}
                        {String(run.status || '').toLowerCase() === 'queued' && <button className="icon-btn" title="Start" onClick={(event) => { event.stopPropagation(); startQueuedRun(run); }}><Icon name="check" size={14} /></button>}
                      </div>
                    </div>
                    {failure && open && (
                      <div className="pd-test3-triage">
                        <div className="summary">
                          <Icon name="alert" size={16} style={{ color: 'var(--neg)', flex: 'none', marginTop: 1 }} />
                          <div>
                            <div className="crumb"><span className="k">Failed at</span><span className="step">{failure.stepLabel}</span></div>
                            {failure.url && <a className="url" href={failure.url} target="_blank" rel="noreferrer"><Icon name="external" size={11} />{failure.url}</a>}
                          </div>
                        </div>
                        {failure.groups.map((group, groupIndex) => (
                          <div className="group" key={groupIndex}>
                            <div className="glabel">{group.label}<span className="cnt">{group.items.length}</span></div>
                            {group.items.map((item, itemIndex) => <pre key={itemIndex}>{stringifyTriageItem(item)}</pre>)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }) : <div className="pd-test3-empty">No runner activity yet.</div>}
            </div>
          </div>

          <div className="pd-test3-side">
            <div className="pd-test3-panel">
              <div className="ph">
                <h2>Test cases</h2>
                <span className="sub">{selectedSuite ? `${(selectedSuite.cases || []).length} in ${selectedSuite.name}` : '—'}</span>
                {selectedSuite && <button className="btn sm" style={{ marginLeft: 'auto' }} onClick={() => startRun(selectedSuite, primaryEnv)}><Icon name="check" size={12} />Run suite</button>}
              </div>
              {selectedSuite && (selectedSuite.cases || []).map((testCase) => {
                const open = !!expandedCases[testCase.id];
                const last = testCase.last || testCase.status;
                return (
                  <div key={testCase.id} className={'pd-test3-case' + (open ? ' open' : '')}>
                    <button className="row" onClick={() => toggleCase(testCase.id)}>
                      <span className="chev"><Icon name="chevR" size={14} /></span>
                      <span className="nm">{caseName(testCase)}</span>
                      <span className="ct">{(testCase.steps || []).length} steps</span>
                      {last && <Pill kind={last} sm />}
                    </button>
                    {open && (
                      <div className="steps">
                        {(testCase.steps || []).map((step, index) => (
                          <div className="s" key={index}>
                            <span className="num">{index + 1}</span>
                            <span className="stxt">{stepLabel(step)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {selectedSuite && (selectedSuite.cases || []).length === 0 && <div className="pd-test3-empty">No cases in this suite.</div>}
              {!selectedSuite && <div className="pd-test3-empty">No suite selected.</div>}
            </div>

            {selectedSuite && (
              <button className="btn danger sm" style={{ justifySelf: 'start' }} onClick={() => removeSuite(selectedSuite)}><Icon name="trash" size={13} />Delete suite</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ---------- shell ---------- */
  function ProjectDetailView({ db, project, currentEmployeeId, actionCenterOverview, bridgeActiveEmployeeIds, bridgeActivityByEmployeeId, bridgeActivityByRunId, onBack, onDrawer, onAction, onToast, onError, onReload, onVoiceContextChange }) {
    const [tab, setTab] = useState(() => projectTabFromHash(project.id));
    const activeTabRef = React.useRef(null);
    const fin = projectFinance(db, project.id);
    const pr = progressOf(project);
    const customerNames = (project.customerIds || []).map((id) => { const customer = (db.customers || []).find((item) => item.id === id); return customer ? customer.name : null; }).filter(Boolean);
    const aiContextReference = projectAiContextReference(project, customerNames, pr, fin);
    const planning = useProjectPlanning(project, onAction);
    const EmailComponent = window.EmailExplorer;
    const projectNotes = (db.notes || []).filter((item) => item.projectId === project.id);
    const contextCount = (project.documents || []).length + (project.emails || []).length + projectNotes.length;
    const requirementCount = MODEL.projectRequirements(project).length;
    const specCount = Array.isArray(project.specs) ? project.specs.length : null;
    const screenDraftCount = (project.screenDrafts || []).length;
    const sketchCount = (project.sketches || []).length;
    const designGuidelineCount = (project.implementationGuidelineTemplateIds || []).length
      + (project.designGuidelineTemplateIds || []).length
      + (project.workPackages || []).reduce((sum, workPackage) => sum + ((workPackage.implementationGuidelineTemplateIds || []).length) + ((workPackage.designGuidelineTemplateIds || []).length), 0);
    const milestoneCount = (project.milestones || []).length;
    const hasFinalizedInvoices = fin.invs.some((iv) => iv.status !== 'Draft' || iv.finalizedAt);
    const showSoftwareFields = window.AC.projectHasSoftwareFields(project);
    const [localAppStatus, setLocalAppStatus] = useState(null);
    const [localAppBusy, setLocalAppBusy] = useState(false);
    const localAppTabRef = React.useRef(null);
    const localAppErrorRef = React.useRef('');

    // inline project field save → new onAction case (see README)
    const saveProject = (patch) => onAction('updateProject', { projectId: project.id, project, patch });
    async function deleteProject() { if (await onAction('deleteProject', project)) onBack(); }
    function localAppMessageTab(tab, title, message) {
      if (!tab || tab.closed) return;
      tab.document.title = title;
      tab.document.body.style.cssText = 'font:14px system-ui,sans-serif;margin:32px;line-height:1.45;color:#172033;background:#f8fafc;';
      tab.document.body.innerHTML = `<h1 style="font-size:20px;margin:0 0 12px;">${title}</h1><pre style="white-space:pre-wrap;background:#fff;border:1px solid #d8dee8;border-radius:8px;padding:16px;">${String(message).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]))}</pre>`;
    }

    const loadLocalAppStatus = React.useCallback(async () => {
      if (!showSoftwareFields || !window.API.getProjectLocalAppStatus) {
        setLocalAppStatus(null);
        return;
      }

      try {
        setLocalAppStatus(await window.API.getProjectLocalAppStatus(project.id));
      } catch {
        setLocalAppStatus(null);
      }
    }, [project.id, showSoftwareFields]);

    async function toggleLocalApp() {
      if (!localAppStatus || localAppBusy) return;
      const running = !!localAppStatus.running;
      const browserTab = !running ? window.open('about:blank', '_blank') : null;
      if (browserTab) {
        localAppTabRef.current = browserTab;
        localAppMessageTab(browserTab, 'Starting local app', 'Waiting for Appligator Companion...');
      }
      try {
        setLocalAppBusy(true);
        const nextStatus = running
          ? await window.API.stopProjectLocalApp(project.id)
          : await window.API.startProjectLocalApp(project.id);
        setLocalAppStatus(nextStatus);
        if (!running && nextStatus && nextStatus.running) {
          const targetUrl = (nextStatus && nextStatus.targetUrl) || localAppStatus.targetUrl;
          if (localAppTabRef.current && targetUrl) localAppTabRef.current.location.href = targetUrl;
          else if (targetUrl) window.open(targetUrl, '_blank');
        }
        if (onToast) onToast(running ? 'Local app stopped.' : nextStatus?.pending ? 'Starting local app...' : 'Local app started.');
      } catch (e) {
        const message = (e && e.message) || (running ? 'Failed to stop local app.' : 'Failed to start local app.');
        localAppMessageTab(localAppTabRef.current || browserTab, 'Local app failed to start', message);
        if (onError) onError(message);
      } finally {
        setLocalAppBusy(false);
      }
    }

    const statItems = [
      { k: 'Total billed', v: money(fin.billed) },
      { k: 'Outstanding', v: fin.outstanding > 0 ? money(fin.outstanding) : '—', tone: fin.outstanding > 0 ? 'warn' : '' },
      { k: 'Invoices', v: String(fin.invs.length) },
      { k: 'Progress', v: Math.round(pr.msPct * 100) + '%', tone: 'pos' },
    ];

    const selectTab = (id) => {
      setTab(id);
      const nextHash = `#/projects/${encodeURIComponent(project.id)}/${id}`;
      if (window.location.hash !== nextHash) window.location.hash = nextHash;
    };

    const tabs = [
      { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
      { id: 'context', label: 'Context', icon: 'inbox', count: contextCount },
      { id: 'design', label: 'Design', icon: 'fileImage', count: requirementCount + screenDraftCount + sketchCount + designGuidelineCount + milestoneCount },
      showSoftwareFields && { id: 'specs', label: 'Specs', icon: 'doc', count: specCount },
      { id: 'board', label: showSoftwareFields ? 'Board' : 'To do', icon: 'dashboard', count: pr.ticketTotal },
      showSoftwareFields && { id: 'testing', label: 'Testing', icon: 'check' },
      { id: 'calendar', label: 'Calendar', icon: 'calendar' },
      { id: 'email', label: 'Email', icon: 'mail', count: (project.emails || []).length },
      { id: 'financials', label: 'Financials', icon: 'invoices', count: fin.invs.length + fin.expenses.length },
    ].filter(Boolean);

    React.useEffect(() => {
      if (!tabs.some((item) => item.id === tab)) selectTab('dashboard');
    }, [tab, showSoftwareFields, project.id]);

    React.useEffect(() => {
      const syncTab = () => {
        setTab(projectTabFromHash(project.id));
        const legacyRequirementsHash = `#/projects/${encodeURIComponent(project.id)}/requirements`;
        if (window.location.hash === legacyRequirementsHash) {
          window.history.replaceState(null, '', `#/projects/${encodeURIComponent(project.id)}/design`);
        }
      };
      syncTab();
      window.addEventListener('hashchange', syncTab);
      return () => window.removeEventListener('hashchange', syncTab);
    }, [project.id]);

    React.useEffect(() => {
      if (activeTabRef.current) activeTabRef.current.scrollIntoView({ block: 'nearest', inline: 'center' });
    }, [tab]);
    React.useEffect(() => { loadLocalAppStatus(); }, [loadLocalAppStatus]);
    React.useEffect(() => {
      if (!localAppStatus?.pending) return undefined;
      const timer = window.setInterval(loadLocalAppStatus, 1000);
      return () => window.clearInterval(timer);
    }, [localAppStatus?.pending, loadLocalAppStatus]);
    React.useEffect(() => {
      if (!localAppStatus) return;
      if (localAppStatus.running && localAppTabRef.current && !localAppTabRef.current.closed) {
        localAppTabRef.current.location.href = localAppStatus.targetUrl;
        localAppTabRef.current = null;
        localAppErrorRef.current = '';
      }
      if (localAppStatus.error && localAppStatus.error !== localAppErrorRef.current) {
        localAppErrorRef.current = localAppStatus.error;
        localAppMessageTab(localAppTabRef.current, 'Local app failed to start', localAppStatus.error);
        if (onError) onError(localAppStatus.error);
      }
    }, [localAppStatus, onError]);
    React.useEffect(() => {
      if (!window.API.subscribeToChanges || !showSoftwareFields) return undefined;
      let timer = null;
      const unsubscribe = window.API.subscribeToChanges((change) => {
        if (!change || change.toolName !== 'testing') return;
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          timer = null;
          loadLocalAppStatus();
        }, 100);
      });
      return () => {
        if (timer) window.clearTimeout(timer);
        unsubscribe();
      };
    }, [loadLocalAppStatus, showSoftwareFields]);

    const localAppRunning = !!localAppStatus?.running;
    const localAppPending = !!localAppStatus?.pending;
    const localAppCanStart = !!localAppStatus?.canStart;
    const localAppDisabled = localAppBusy || localAppPending || (localAppRunning ? !localAppStatus?.hasStopCommand : !localAppCanStart);
    const localAppTitle = localAppStatus
      ? localAppPending
        ? (localAppStatus.pendingAction === 'stop' ? 'Stopping local app' : 'Starting local app')
        : localAppRunning
        ? 'Stop local app'
        : localAppCanStart
        ? 'Start local app'
        : 'Configure start and stop commands in Testing first'
      : 'Configure start and stop commands in Testing first';

    return (
      <div className="pd-root">
        <div className="pd-topbar">
          <div className="pd-tabbar">
            {tabs.map((t) => (
              <button key={t.id} ref={tab === t.id ? activeTabRef : null} className={'pd-tab' + (tab === t.id ? ' on' : '')} onClick={() => selectTab(t.id)}>
                <Icon name={t.icon} size={14} />{t.label}{t.count != null && <span className="pd-tabcount">{t.count}</span>}
              </button>
            ))}
          </div>
          <div className="pd-actions">
            {showSoftwareFields && (
              <button
                className={'btn sm ' + (localAppRunning ? 'danger' : 'ghost')}
                type="button"
                disabled={localAppDisabled}
                title={localAppTitle}
                onClick={toggleLocalApp}
              >
                <Icon name={localAppRunning ? 'close' : 'play'} size={14} />{localAppBusy || localAppPending ? (localAppStatus?.pendingAction === 'stop' || localAppRunning ? 'Stopping...' : 'Starting...') : (localAppRunning ? 'Stop app' : 'Start app')}
              </button>
            )}
            <AiEmployeeContextActions db={db} onToast={onToast} onError={onError}>
              {({ openContextPicker }) => (
                <button className="btn ghost sm" type="button" title="Ask an AI employee about this project" onClick={(event) => openContextPicker(event, aiContextReference)}>
                  <Icon name="server" size={14} />Ask AI
                </button>
              )}
            </AiEmployeeContextActions>
            <button className="btn ghost sm" onClick={() => onDrawer('project', project.id)}><Icon name="edit" size={14} />Edit</button>
            <button className="btn ghost sm" disabled={hasFinalizedInvoices} title={hasFinalizedInvoices ? 'Project has finalized invoices' : 'Delete project'} onClick={deleteProject}><Icon name="trash" size={14} />Delete</button>
          </div>
        </div>

        <div className={'pd-scroll' + (tab === 'board' ? ' pd-board-scroll' : '')}>
          {tab === 'dashboard' && (
            <div>
              {showSoftwareFields && <WorkPackagePipelinePanel project={project} onAction={onAction} />}
              <div className="grid pd-dashboard-grid pd-dashboard-redesign">
                <div className="pd-dashboard-column">
                  <CompactOverviewPanel project={project} db={db} saveProject={saveProject} onDrawer={onDrawer} onAction={onAction} />
                </div>
                <div className="pd-dashboard-column pd-dashboard-right">
                  <ProjectActionCenter project={project} db={db} onSelectTab={selectTab} />
                  <EventOverview project={project} db={db} />
                </div>
              </div>
            </div>
          )}
          {(tab === 'context' || tab === 'design' || tab === 'board') && <PlanningSaveBar planning={planning} />}
          {tab === 'context' && <ProjectContextTab planning={planning} project={project} db={db} currentEmployeeId={currentEmployeeId} actionCenterOverview={actionCenterOverview} bridgeActiveEmployeeIds={bridgeActiveEmployeeIds} bridgeActivityByEmployeeId={bridgeActivityByEmployeeId} bridgeActivityByRunId={bridgeActivityByRunId} onDrawer={onDrawer} onAction={onAction} onSelectTab={selectTab} onToast={onToast} onError={onError} onReload={onReload} Inline={Inline} />}
          {tab === 'design' && <ProjectRequirementsTab planning={planning} project={project} db={db} onDrawer={onDrawer} onAction={onAction} onToast={onToast} onError={onError} onReload={onReload} onVoiceContextChange={onVoiceContextChange} Inline={Inline} />}
          {tab === 'specs' && showSoftwareFields && <ProjectSpecsTab project={project} onAction={onAction} onToast={onToast} onError={onError} />}
          {tab === 'board' && <ProjectBoardTab planning={planning} project={project} db={db} statuses={window.AC.projectTicketStatuses(project)} onAction={onAction} onToast={onToast} onError={onError} Inline={Inline} />}
          {tab === 'testing' && showSoftwareFields && <ProjectTestingTab project={project} onAction={onAction} />}
          {tab === 'calendar' && <CalendarPane db={db} scopeProjectId={project.id} currentEmployeeId={currentEmployeeId} title={`${project.name} calendar`} defaultView="week" showScopeSelector={false} onToast={onToast} onError={onError} />}
          {tab === 'email' && EmailComponent && <EmailComponent db={db} query="" onToast={onToast} onError={onError} onReload={onReload} onDrawer={onDrawer} onAction={onAction} scopeId={project.id} scopeProject={project} showProjectDrop={false} />}
          {tab === 'financials' && <FinancialsTab project={project} db={db} fin={fin} onDrawer={onDrawer} onAction={onAction} />}
        </div>
      </div>
    );
  }

  Object.assign(window, { ProjectDetailView, ProjectDocumentArea, projectDocumentsFor, projectDocumentDropProps, projectDocumentTitle, editProjectDocumentDescription, projectAiContextReference, ProjectProgress: progressOf, WorkPackagePhaseStepper });
})();
